import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import type { SessionState, TokenClaims } from 'src/services/SessionService';

const stringClaim = (claims: TokenClaims, key: string): string | undefined => {
    const value = claims[key];
    return typeof value === 'string' ? value : undefined;
};

const hasRealmRole = (claims: TokenClaims, role: string): boolean => {
    const realmAccess = claims.realm_access;
    if (!realmAccess || typeof realmAccess !== 'object' || !('roles' in realmAccess)) return false;
    const roles = realmAccess.roles;
    return Array.isArray(roles) && roles.includes(role);
};

/**
 * Identity is a projection of the authoritative session claims, not a second
 * session state. It is intentionally usable in both main and renderer hosts.
 */
export const sessionUserDetails = (state: SessionState): UserDetails | undefined => {
    if (state.phase !== 'authenticated' && state.phase !== 'refreshing') return undefined;

    const claims = state.claims;
    const id = stringClaim(claims, 'userId') ?? stringClaim(claims, 'sub');
    if (!id) return undefined;

    const firstName = stringClaim(claims, 'given_name') ?? '';
    const lastName = stringClaim(claims, 'family_name') ?? '';

    return {
        id,
        name: stringClaim(claims, 'name') ?? `${firstName} ${lastName}`.trim(),
        email: stringClaim(claims, 'email') ?? '',
        firstName,
        lastName,
        isSuperuser: hasRealmRole(claims, 'superuser') || claims.isSuperuser === true,
        isDeveloper: hasRealmRole(claims, 'developer') || claims.isDeveloper === true,
    };
};
